import { REVIEW_TEST_IDS } from '@/components/admin/sessions/contracts';
import { REVIEW_NOTE_MAX } from '@matter_extractor/shared';
import { Button, TextArea } from '@pms-digital/design-system';
import { type ReactNode, useEffect, useRef } from 'react';

/**
 * The small editor every note of the review opens in place (relecture.md §5.5,
 * §6, §8, §9): a field, a button that says what it does, and « Annuler ».
 *
 * Uncontrolled, like the forms of this package: the field is read when the
 * button is pressed, and the caller decides what the text means - an empty one
 * clears a note, but cannot add one.
 *
 * It takes the focus when it opens, once: the gesture that opened it was a click
 * on « noter », and the next thing anybody does is type.
 */

export interface ReviewNoteEditorProps {
  /** The accessible name of the field - « Note de la case », « Note de l'extrait ». */
  readonly label: string;
  readonly initial?: string;
  readonly placeholder?: string;
  /** What the confirming button says: « Enregistrer », « Je valide »... */
  readonly confirm?: string;
  /**
   * The tone of the confirming button: a validation says « check », a refusal
   * « close ». The package's buttons have no green or red outline, so the tone
   * is carried by the icon.
   */
  readonly tone?: 'default' | 'ok' | 'ko';
  readonly max?: number;
  readonly busy: boolean;
  readonly onSave: (text: string) => void;
  readonly onCancel: () => void;
  readonly children?: ReactNode;
}

const TONE_ICON = { default: undefined, ok: 'check', ko: 'close' } as const;

export function ReviewNoteEditor({
  label,
  initial = '',
  placeholder,
  confirm = 'Enregistrer',
  tone = 'default',
  max = REVIEW_NOTE_MAX,
  busy,
  onSave,
  onCancel,
  children,
}: ReviewNoteEditorProps) {
  const field = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    field.current?.focus({ preventScroll: true });
  }, []);

  return (
    <div className="flex flex-col gap-1.5">
      <TextArea
        ref={field}
        data-testid={REVIEW_TEST_IDS.noteInput}
        label={label}
        hideLabel={true}
        defaultValue={initial}
        placeholder={placeholder}
        maxLength={max}
        rows={2}
      />
      {children}
      <div className="flex flex-wrap gap-1.5">
        <Button
          type="button"
          size="xs"
          compact={true}
          variant="accent"
          {...(TONE_ICON[tone] === undefined ? {} : { icon: TONE_ICON[tone] })}
          data-testid={REVIEW_TEST_IDS.noteSave}
          disabled={busy}
          onClick={() => onSave(field.current?.value ?? '')}
        >
          {confirm}
        </Button>
        <Button
          type="button"
          size="xs"
          compact={true}
          variant="ghost"
          data-testid={REVIEW_TEST_IDS.noteCancel}
          onClick={onCancel}
        >
          Annuler
        </Button>
      </div>
    </div>
  );
}
